import { useCallback, useEffect, useMemo } from "react"
import { FormProvider, useForm } from "react-hook-form"
import { useTranslation } from "react-i18next"
import { useQueryClient } from "react-query"
import { useNavigate, useParams } from "react-router-dom"
import { FormQueryKeys } from "../../constants/FormQueryKeys"
import { useSectionDispatch } from "../../context/SectionContextProvider"
import { usePatchReferralData } from "../../hooks/usePatchReferralData"
import { AdminMeta } from "../../types/AdminMeta"
import { FormSection } from "../../types/FormSection"
import { Referral } from "../../types/Referral"
import { FormReviewPage } from "../FormReviewPage"
import { FormSection as FormSectionComponent } from "../FormSection"
import { ReferralReviewSidebar } from "./ReferralReviewSidebar"
import { useFlags } from "launchdarkly-react-client-sdk"
import { FeatureFlagConfig } from "../../types/FeatureFlags"
import { ReferralReviewTable } from "./ReferralReviewTable"
import classNames from "classnames"

interface Props {
  form: FormSection[]
  defaultValues: Referral
  edit?: boolean
  referralId?: string
  referral_status?: Array<{
    id: number
    label: Referral["status"]
    order: number
  }>
  adminMeta?: AdminMeta
  setError?: (error: string | undefined) => unknown
}

export function ReferralReviewForm({
  form,
  defaultValues,
  edit,
  referral_status,
  adminMeta,
  setError,
}: Props) {
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  const { useReferralVersion2 } = useFlags<FeatureFlagConfig>()
  const { patchReferralData } = usePatchReferralData(useReferralVersion2)
  const { referralId } = useParams<{ referralId: string }>()
  const navigate = useNavigate()
  const dispatch = useSectionDispatch()
  const useMdsV2 =
    useReferralVersion2 &&
    defaultValues?.client_province?.toLowerCase() === "ontario"

  const normalizedDefaultValues = useMemo(
    () =>
      Object.keys(defaultValues).reduce((prev, key) => {
        const value = defaultValues[key as keyof Referral]
        if (
          value !== null &&
          (!Array.isArray(value) ||
            value.filter((val) => val !== null).length > 0)
        ) {
          return { ...prev, [key]: value }
        } else {
          return prev
        }
      }, {} as Referral),
    [defaultValues],
  )

  const refetch = useCallback(async () => {
    if (referralId) {
      await queryClient.invalidateQueries(FormQueryKeys.byId(referralId))
    }
  }, [queryClient, referralId])

  const methods = useForm({
    defaultValues: normalizedDefaultValues,
  })

  useEffect(() => {
    form && dispatch({ type: "SET_SECTIONS", sections: form })
  }, [form, dispatch])

  const handleCancel = useCallback(() => {
    methods.reset()
    navigate(`../${referralId}`)
  }, [methods, navigate, referralId])

  const handleSave = useCallback(() => {
    if (!referralId) return

    methods.handleSubmit(
      (values) => {
        patchReferralData(
          { id: referralId, referralData: values },
          {
            onError: () => {
              setError?.(t("admin.error.defaultError"))
            },
            onSuccess: async () => {
              await refetch()
              navigate(`../${referralId}`)
            },
          },
        )
      },
      () => {
        methods.trigger()
      },
    )()
  }, [methods, navigate, patchReferralData, referralId, setError, refetch, t])

  const handleUpdateStatus = useCallback(
    (nextStatus: Referral["status"]) => {
      if (!referral_status) {
        return
      }

      const apiStatus = referral_status.find(
        (status) => status.label.toLowerCase() === nextStatus.toLowerCase(),
      )

      if (apiStatus && referralId) {
        patchReferralData(
          {
            id: referralId,
            referralData: {
              ...defaultValues,
              referral_status_id: apiStatus.id,
            },
          },
          {
            onSuccess: () => refetch(),
            onError: () => {
              setError?.(t("admin.error.defaultError"))
            },
          },
        )
      }
    },
    [
      defaultValues,
      patchReferralData,
      referralId,
      referral_status,
      refetch,
      setError,
      t,
    ],
  )

  if (!form) {
    return null
  }

  return (
    <>
      <ReferralReviewSidebar
        useReferralVersion2={useMdsV2}
        referral={defaultValues}
        adminMeta={adminMeta}
        edit={edit}
        onCancel={handleCancel}
        onSave={handleSave}
        onUpdateStatus={handleUpdateStatus}
      />
      <div className={classNames({ "w-full": useMdsV2 })}>
        {useMdsV2 ? (
          <ReferralReviewTable clientData={defaultValues} />
        ) : (
          <FormProvider {...methods}>
            {form?.map((formSection) => (
              <FormSectionComponent
                key={"form-" + formSection.section_name}
                section={formSection}
                isHidden={!edit}
              />
            ))}
            {!edit ? (
              <FormReviewPage hideHeaderText={true} hideEditButtons={true} />
            ) : null}
          </FormProvider>
        )}
      </div>
    </>
  )
}
